import React, { useEffect, useMemo, useState } from 'react';
import ReactGA from 'react-ga4';
import Search from './Search';
import CountyResults from './CountyResults';
import MapComponent from './MapComponent';
import StateList from './StateList';
import { CountyOption, StateMetadata } from 'interfaces/StateMetadata';
import { SearchResult } from 'interfaces/SearchTypes';
import { useUrlState } from 'hooks/useUrlState';
import { searchStatesAndCounties } from 'utils/searchUtils';
import debounce from 'util/debounce';
import 'stylesheets/ballottrax-finder.css';

// How long a searcher has to stop typing before an empty result set counts as a
// real miss - long enough that "Cal" on the way to "California" is not reported
const NO_RESULT_REPORT_DELAY_MS = 800;

interface BallotTraxFinderProps {
  stateMetadata: { [stateName: string]: StateMetadata };
  counties: CountyOption[];
  isTouchDevice: boolean;
}

const BallotTraxFinder = ({
  stateMetadata,
  counties,
  isTouchDevice,
}: BallotTraxFinderProps) => {
  // URL-aware state management
  const {
    searchQuery,
    selectedState,
    mode,
    setSearchQuery: setUrlSearchQuery,
    setSelectedState: setUrlSelectedState,
    setMode: setUrlMode,
    updateUrl,
  } = useUrlState({ stateMetadata });

  // Local state for UI interactions (not synced to URL)
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [focusedState, setFocusedState] = useState<string | null>(null);
  const [navigationContext, setNavigationContext] = useState<
    'search' | 'results'
  >('search');
  const [shouldAutoScroll, setShouldAutoScroll] = useState<boolean>(false);

  // Convert stateMetadata to array for easier processing
  const statesList = Object.entries(stateMetadata).map(
    ([stateName, metadata]) => ({
      stateName,
      ...metadata,
    }),
  );

  // Reports what people look for that BallotTrax does not cover - the only
  // analytics signal for unmet demand, so it has to survive a search rewrite.
  // Memoized because the debounce timer lives inside the returned function
  const reportNoResultSearch = useMemo(
    () =>
      debounce((query: string, resultCount: number) => {
        if (resultCount === 0 && query.trim().length > 0) {
          ReactGA.event({
            category: 'search',
            action: 'search - no result',
            label: query,
          });
        }
      }, NO_RESULT_REPORT_DELAY_MS),
    [],
  );

  // A miss that never settled is not a miss, and a timer left running would
  // report one after the searcher has already left the page
  useEffect(() => {
    return () => {
      reportNoResultSearch.cancel();
    };
  }, [reportNoResultSearch]);

  // Enhanced search function that finds counties AND states
  const performSearch = (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      setUrlMode('search');
      return [];
    }

    const results = searchStatesAndCounties(query, statesList, counties);
    setSearchResults(results);
    setUrlMode('results');
    return results;
  };

  // Handle search query changes
  const handleSearchChange = (query: string) => {
    // Prevent auto-scroll during typing
    setShouldAutoScroll(false);
    setUrlSearchQuery(query);
    // Clear selected state when performing a new search
    setUrlSelectedState(null);
    setFocusedState(null);

    const results = performSearch(query);

    // Only typed searches are reported. The searchQuery effect below also runs
    // for queries hydrated from ?q= and for back/forward navigation, and
    // counting those would inflate misses with links people merely opened
    reportNoResultSearch(query, results.length);
  };

  // Handle state selection from grid or map
  const handleStateSelect = (stateName: string) => {
    // Update URL with selected state
    updateUrl({ selectedState: stateName, mode: 'results' });

    // Set navigation context based on current mode
    if (mode === 'results' && searchResults.length > 0) {
      // Coming from search results - preserve them
      setNavigationContext('results');
    } else {
      // Coming from main search/browse - clear search results
      setSearchResults([]);
      setNavigationContext('search');
    }
  };

  // Handle state click from map
  const handleMapStateClick = (stateName: string) => {
    handleStateSelect(stateName);
    setShouldAutoScroll(true);
  };

  // Handle focus changes for map highlighting
  const handleStateFocus = (stateName: string | null) => {
    setFocusedState(stateName);
  };

  // Handle hierarchical back navigation
  const handleBack = () => {
    if (selectedState && navigationContext === 'results') {
      // Go back to search results, preserving them
      updateUrl({ selectedState: null, mode: 'results' });
      setFocusedState(null);
    } else {
      // Go back to main search/browse
      updateUrl({ searchQuery: '', selectedState: null, mode: 'search' });
      setSearchResults([]);
      setFocusedState(null);
      setNavigationContext('search');

      // Smooth scroll to search bar first, then focus
      const searchInput = document.querySelector(
        '#search-input',
      ) as HTMLInputElement;
      if (searchInput) {
        searchInput.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest',
        });

        // Focus after scroll completes
        setTimeout(() => {
          searchInput.focus();
        }, 600);
      }
    }
  };

  // Effect to handle URL state changes and perform search when needed
  useEffect(() => {
    if (searchQuery.trim()) {
      performSearch(searchQuery);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]); // Only re-run when searchQuery changes

  // Effect to auto-scroll to results when loading from URL or deliberate actions
  useEffect(() => {
    // Only auto-scroll if we should and we're in results mode
    if (shouldAutoScroll && mode === 'results') {
      // Add a delay to ensure the UI has rendered
      setTimeout(() => {
        if (searchResults.length > 0 || selectedState) {
          const resultsElement = document.querySelector(
            '[data-results-section]',
          );
          if (resultsElement) {
            resultsElement.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
              inline: 'nearest',
            });
          }

          // Focus the first result after scrolling completes (only for Enter key)
          setTimeout(() => {
            const firstResult = document.querySelector(
              '[data-results-section] a, [data-results-section] button',
            );
            if (firstResult instanceof HTMLElement) {
              firstResult.focus();
            }
          }, 600);
        } else {
          // No results - scroll to search bar
          const searchInput = document.querySelector(
            '#search-input',
          ) as HTMLInputElement;
          if (searchInput) {
            searchInput.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
              inline: 'nearest',
            });
          }
        }
        // Reset the flag after scrolling
        setShouldAutoScroll(false);
      }, 300);
    }
  }, [shouldAutoScroll, mode, searchResults.length, selectedState]); // Auto-scroll when flag is set

  // Effect to sync navigationContext with URL state
  useEffect(() => {
    if (mode === 'search') {
      setNavigationContext('search');
    }
  }, [mode]);

  // Effect to trigger auto-scroll when URL loads with results (for shareable URLs)
  useEffect(() => {
    // Only trigger on initial load or when mode changes to results, not on every search query change
    if (mode === 'results' && (searchQuery.trim() || selectedState)) {
      // We can detect this by checking if the search query matches what's in URL exactly
      const urlParams = new URLSearchParams(window.location.search);
      const urlQuery = urlParams.get('q') || '';
      const urlState = urlParams.get('state') || '';

      // Only set auto-scroll if URL exactly matches AND this is an initial page load
      if (
        (urlQuery && urlQuery === searchQuery.trim()) ||
        (urlState && urlState === selectedState)
      ) {
        setShouldAutoScroll(true);
      }
    }
  }, [mode]); // Only trigger when mode changes, not on every search query change

  const handleSearchEnterPress = () => {
    if (mode === 'results') {
      setShouldAutoScroll(true);
    }
  };

  return (
    <div className="ballottrax-finder">
      <Search
        query={searchQuery}
        onQueryChange={handleSearchChange}
        onClear={handleBack}
        onEnterPress={handleSearchEnterPress}
        placeholder="Search by county or state (e.g., 'San Francisco' or 'California')"
      />

      {/* No results message - show when user searched but found nothing */}
      {mode === 'results' &&
        searchResults.length === 0 &&
        !selectedState &&
        searchQuery.trim() && (
          <div className="no-results-message" data-no-results="true">
            <strong>
              Unfortunately, BallotTrax is not available in {searchQuery} at
              this time.
            </strong>
            <br />
            Try searching for a different county, state name, or state
            abbreviation.
          </div>
        )}

      <MapComponent
        stateMetadata={stateMetadata}
        counties={counties}
        highlightedState={focusedState || selectedState}
        onStateClick={handleMapStateClick}
        isTouchDevice={isTouchDevice}
        searchResults={mode === 'results' ? searchResults : []}
        mode={mode}
      />

      {mode === 'search' && (
        <StateList
          stateMetadata={stateMetadata}
          counties={counties}
          onStateSelect={handleStateSelect}
          searchResults={searchResults}
        />
      )}

      {mode === 'results' && (
        <div className="results-container" data-results-section={false}>
          <CountyResults
            searchResults={searchResults}
            selectedState={selectedState}
            counties={counties}
            onClear={handleBack}
            onStateSelect={handleStateSelect}
            onStateFocus={handleStateFocus}
            isTouchDevice={isTouchDevice}
            stateMetadata={stateMetadata}
            navigationContext={navigationContext}
          />
        </div>
      )}
    </div>
  );
};

export default BallotTraxFinder;
